import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Lock } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const FieldInput = ({ fd, value, onChange, isSet }) => (
  <div className={`space-y-1 ${fd.type === "textarea" ? "sm:col-span-2" : ""}`}>
    <Label className="text-xs">{fd.label}{fd.required && !isSet && <span className="text-rose-500"> *</span>}{fd.secret && <Lock className="ml-1 inline h-3 w-3 text-slate-400" />}</Label>
    {fd.type === "textarea"
      ? <Textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={isSet ? "Stored securely — leave blank to keep" : fd.placeholder} className="font-mono text-xs" data-testid={`ds-field-${fd.name}`} />
      : <Input type={fd.secret ? "password" : fd.type === "number" ? "number" : "text"} autoComplete="off" value={value || ""} onChange={(e) => onChange(e.target.value)}
        placeholder={isSet ? "Stored securely — leave blank to keep" : fd.placeholder} data-testid={`ds-field-${fd.name}`} />}
    {fd.help && <p className="text-[11px] text-slate-400">{fd.help}</p>}
  </div>
);

export function ConnectDialog({ open, onClose, onSaved, existing }) {
  const [types, setTypes] = useState([]);
  const [envs, setEnvs] = useState([]);
  const [type, setType] = useState(null);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/data-sources/types").then((r) => { setTypes(r.data.connectors); setEnvs(r.data.environments); }); }, [open]);
  useEffect(() => {
    if (!open) { setType(null); setF({}); return; }
    if (existing) { setType(existing.type); setF({ name: existing.name, environment: existing.environment, owner: existing.owner, auth_method: existing.auth_method, ...existing.config }); }
  }, [open, existing]);
  const meta = types.find((t) => t.key === type);
  const auth = meta?.auth_methods.find((a) => a.key === f.auth_method) || meta?.auth_methods[0];
  const fields = meta ? [...meta.config_fields, ...(auth?.fields || [])] : [];
  const isSet = (n) => existing?.auth_method === auth?.key && existing?.secret_fields_set?.includes(n);
  const save = async () => {
    setBusy(true);
    const config = {}, secrets = {};
    fields.forEach((x) => { if (f[x.name] !== undefined) (x.secret ? secrets : config)[x.name] = f[x.name]; });
    const body = { name: f.name || "", type, auth_method: auth.key, environment: f.environment || "Production", owner: f.owner || "", config, secrets };
    try {
      const { data } = existing ? await api.put(`/data-sources/${existing.id}`, body) : await api.post("/data-sources", body);
      const t = await api.post(`/data-sources/${data.id}/test`);
      if (t.data.status === "Connected") toast.success("Connected — credentials stored encrypted"); else toast.error(`Saved, but the connection test failed: ${t.data.last_error}`);
      onSaved(t.data);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto" data-testid="connect-dialog">
        <DialogHeader><DialogTitle>{existing ? "Edit data source" : "Connect data source"}</DialogTitle>
          <DialogDescription>Read-only access only. Secrets are encrypted at rest and never shown again. Scans keep statistics only — no data values are stored.</DialogDescription></DialogHeader>
        {!type ? (
          <div className="grid gap-2 sm:grid-cols-3" data-testid="connector-grid">
            {types.map((t) => (
              <button key={t.key} disabled={t.status !== "Available"} onClick={() => { setType(t.key); setF({ auth_method: t.auth_methods[0]?.key, environment: "Production" }); }}
                className="rounded-lg border border-slate-200 p-3 text-left transition-colors hover:border-blue-400 hover:bg-blue-50/40 disabled:cursor-not-allowed disabled:opacity-50" data-testid={`connector-${t.key}`}>
                <div className="text-sm font-semibold text-slate-800">{t.name}</div>
                <div className="text-[11px] text-slate-500">{t.category}{t.status !== "Available" && " · Coming soon"}</div>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1"><Label className="text-xs">Name *</Label><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={`${meta?.name} – customer DB`} data-testid="ds-name" /></div>
              <div className="space-y-1"><Label className="text-xs">Environment</Label>
                <Select value={f.environment || "Production"} onValueChange={(v) => setF({ ...f, environment: v })}><SelectTrigger data-testid="ds-environment"><SelectValue /></SelectTrigger>
                  <SelectContent>{envs.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1"><Label className="text-xs">Owner</Label><Input value={f.owner || ""} onChange={(e) => setF({ ...f, owner: e.target.value })} data-testid="ds-owner" /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Authentication method</Label>
              <Select value={auth?.key} onValueChange={(v) => setF({ ...f, auth_method: v })}><SelectTrigger data-testid="ds-auth-method"><SelectValue /></SelectTrigger>
                <SelectContent>{meta?.auth_methods.map((a) => <SelectItem key={a.key} value={a.key} data-testid={`ds-auth-${a.key}`}>{a.label}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid gap-3 sm:grid-cols-2">{fields.map((x) => <FieldInput key={x.name} fd={x} value={f[x.name]} isSet={isSet(x.name)} onChange={(v) => setF({ ...f, [x.name]: v })} />)}</div>
            <div className="flex gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="ds-readonly-guidance"><ShieldCheck className="h-4 w-4 shrink-0" />{meta?.read_only_guidance}</div>
          </div>
        )}
        <DialogFooter>
          {type && !existing && <Button variant="ghost" onClick={() => setType(null)} data-testid="ds-back">Back</Button>}
          {type && <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="ds-save-test">{busy ? "Testing connection…" : "Save & test connection"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
